import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import ReactTable from 'react-table-v6';
import { Button, Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Edit } from 'react-feather';
import 'react-table-v6/react-table.css';
import ComponentCard from '../../components/ComponentCard';

const AirtelPaymentPendingSites = () => {
  const [modal, setModal] = useState(false);
  const [obj, setObj] = useState({});
  const [projects, setProjects] = useState([]);
  const user = JSON.parse(localStorage.getItem('user'));
  const isAdmin =
    user?.type === 'admin' || user?.type === 'management' || user?.type === 'vendor';

  const toggle = () => setModal(!modal);
  const navigate = useNavigate();

  // Fetch projects
  const fetchProjects = async () => {
    try {
      const response = await axios.get('https://api.outdoorooh.com/api/get-fresh-sites', {
        headers: { 'Content-Type': 'application/json' },
      });
      setProjects(response.data.data);
    } catch (error) {
      console.log('Error fetching projects:', error);
    }
  };

  useEffect(() => {
    const token = user?.token;
    if (!token) {
      navigate('/auth/loginformik');
      return;
    }
    fetchProjects();
  }, []);

  // Export to Excel
  const baseUrl = 'https://api.outdoorooh.com/';
  const exportToExcel = () => {
    const exportData = projects.map((project) => ({
      location: project.location,
      site_code: project.site_code,
      area: project.area,
      district: project.district,
      state: project.state,
      zone: project.zone,
      vendor: project.vendor,
      height: project.height,
      width: project.width,
      total: project.total,
      unit: project.unit,
      media_type: project.media_type,
      signature: project.signature,
      racce_person_name: project.racce_person_name,
      racce_person_image: {
        f: `HYPERLINK("${baseUrl}${project.racce_person_image}", "${baseUrl}${project.racce_person_image}")`,
      },
      image1: { f: `HYPERLINK("${baseUrl}${project.image1}", "${baseUrl}${project.image1}")` },
      image2: { f: `HYPERLINK("${baseUrl}${project.image2}", "${baseUrl}${project.image2}")` },
      image3: { f: `HYPERLINK("${baseUrl}${project.image3}", "${baseUrl}${project.image3}")` },
      image4: { f: `HYPERLINK("${baseUrl}${project.image4}", "${baseUrl}${project.image4}")` },
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Admin Project');
    XLSX.writeFile(workbook, 'projects_data.xlsx');
  };

  // Submit only images + project_id
  const handleSubmit = async (event) => {
    event.preventDefault();

    const formData = new FormData();
    formData.append('project_id', obj.id);

    if (event.target.image.files[0]) {
      formData.append('image', event.target.image.files[0]);
    }
    if (event.target.image1.files[0]) {
      formData.append('image1', event.target.image1.files[0]);
    }
    if (event.target.image2.files[0]) {
      formData.append('image2', event.target.image2.files[0]);
    }
    if (event.target.image3.files[0]) {
      formData.append('image3', event.target.image3.files[0]);
    }
    if (event.target.image4.files[0]) {
      formData.append('image4', event.target.image4.files[0]);
    }

    try {
      const response = await axios.post(
        `https://api.outdoorooh.com/api/fresh-site-upload-pending-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      console.log('Project images uploaded successfully!', response);

      // Update project state if needed
      setProjects((prev) =>
        prev.map((proj) => (proj.id === obj.id ? { ...proj, ...response.data } : proj))
      );
    } catch (error) {
      console.error('Error uploading images:', error);
    }

    setModal(false);
  };

  const defaultFilterMethod = (filter, row) => {
    return String(row[filter.id]).toLowerCase().includes(filter.value.toLowerCase());
  };

  const tableData = useMemo(() => {
    return projects.map((prop) => ({
      ...prop,
      site_code: (
        <Link
          to={`/project-admin-details/${prop.site_code.replace('_', '')}`}
          style={{ textDecoration: 'none', color: '#343a40' }}
        >
          {prop.site_code}
        </Link>
      ),
      location: (
        <Link
          to={`/project-admin-details/${prop.site_code.replace('_', '')}`}
          style={{ textDecoration: 'none', color: '#343a40' }}
        >
          {prop.location}
        </Link>
      ),
      vendor: (
        <Link
          to={`/vendor-details/${prop.vendor}`}
          style={{ textDecoration: 'none', color: '#343a40' }}
        >
          {prop.vendor}
        </Link>
      ),
      actions: isAdmin ? (
        <div className="text-center">
          <Button
            onClick={() => {
              setObj({ id: prop.id, ...prop });
              toggle();
            }}
            color="inverse"
            size="sm"
          >
            <Edit />
          </Button>
        </div>
      ) : null,
    }));
  }, [projects, isAdmin]);

  return (
    <div>
      <div className="d-flex justify-content-end">
        <Button color="success mb-2" onClick={exportToExcel}>
          Export to Excel
        </Button>
      </div>

      {/* Upload Images Modal */}
      <Modal isOpen={modal} toggle={toggle}>
        <ModalHeader toggle={toggle}>Upload Project Images</ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label for="image">Main Image</Label>
              <Input type="file" name="image" id="image" required />
            </FormGroup>
            <FormGroup>
              <Label for="image1">Image1</Label>
              <Input type="file" name="image1" id="image1" />
            </FormGroup>
            <FormGroup>
              <Label for="image2">Image2</Label>
              <Input type="file" name="image2" id="image2" />
            </FormGroup>
            <FormGroup>
              <Label for="image3">Image3</Label>
              <Input type="file" name="image3" id="image3" />
            </FormGroup>
            <FormGroup>
              <Label for="image4">Image4</Label>
              <Input type="file" name="image4" id="image4" />
            </FormGroup>

            <FormGroup>
              <Button color="primary" type="submit">
                Save
              </Button>
              <Button color="secondary" className="ml-1" onClick={toggle}>
                Cancel
              </Button>
            </FormGroup>
          </Form>
        </ModalBody>
      </Modal>

      {/* Projects Table */}
      <ComponentCard title="Projects Table">
        <ReactTable
          data={tableData}
          columns={[
            {
              Header: 'Site Name',
              accessor: 'location',
              filterMethod: (filter, row) => {
                const rowValue =
                  typeof row[filter.id] === 'string'
                    ? row[filter.id]
                    : row[filter.id]?.props?.children || '';
                return rowValue.toLowerCase().includes(filter.value.toLowerCase());
              },
            },
            {
              Header: 'Code',
              accessor: 'site_code',
              filterMethod: (filter, row) => {
                const rowValue = row[filter.id].props.children;
                return rowValue.toLowerCase().includes(filter.value.toLowerCase());
              },
            },
            { Header: 'Area', accessor: 'area' },
            { Header: 'State', accessor: 'state' },
            { Header: 'Zone', accessor: 'zone' },
            {
              Header: 'Vendor Name',
              accessor: 'vendor',
              filterMethod: (filter, row) => {
                const rowValue =
                  typeof row[filter.id] === 'string'
                    ? row[filter.id]
                    : row[filter.id]?.props?.children || '';
                return rowValue.toLowerCase().includes(filter.value.toLowerCase());
              },
            },
            { Header: 'Height', accessor: 'height' },
            { Header: 'Width', accessor: 'width' },
            { Header: 'Total', accessor: 'total_sqr_ft' },
            { Header: 'Actions', accessor: 'actions', sortable: false, filterable: false },
          ]}
          defaultPageSize={200}
          pageSizeOptions={[10, 20, 100, 200, 500, 2500]}
          showPaginationBottom
          className="-striped -highlight"
          filterable
          defaultFilterMethod={defaultFilterMethod}
        />
      </ComponentCard>
    </div>
  );
};

export default AirtelPaymentPendingSites;
